import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
  buildRegistry,
  checkInTime,
  evaluateMetric,
  formatMetricValue,
  metricColor,
  profileOutputsForDay,
  summaryMetrics,
  workloadFromDay,
  type BoardDay,
  staffingConfigFromRows,
  type MetricDef,
  type MetricRegistry,
  type StaffingConfigSnapshot,
  type StaffingSpec,
} from '@lemontaigu/staff';
import { useMetricRegistry, useStaffingConfig } from '@lemontaigu/staff/react';
import { FeedStamp } from './FeedStamp.js';
import {
  DEMAND_HOTEL_FIELDS,
  DEMAND_RESTAURANT_FIELDS,
  FEED_HINT,
  GUESTS_HINT,
  fmtShortDayLabel,
} from './logic.js';

// « Charge », read-only — the desk's board without the desk's keyboard.
//
// staff.* gets the SAME figures as reception (operator, 2026-09-05: *"add just
// this day view 'Charge' in staff.*"* and *"add the week view 'Charge de la
// semaine' to staff.*"*), and this is the one place they are laid out.
//
// The reception views keep their own markup because theirs is an EDITOR: a live
// draft that moves as you type, an occupancy chain that re-chains from the
// first edited day, a « Dîners » input and a save button. Nothing here types,
// so nothing here drafts — every figure is read straight off the stored
// `BoardDay`, which is why this file is short enough to be worth having rather
// than a parameterised fork of DayView.
//
// It renders reception's `team.css` classes verbatim (staff.* already imports
// that stylesheet), so the two apps cannot drift into two visual languages for
// the same four numbers. The phone rules are `.charge-compact` overrides in
// that same file — a variant of this board, not a second board.

/**
 * The metric registry and the staffing profiles the two boards below evaluate
 * against.
 *
 * Both are CONFIGURATION, not day data: loaded once per view, and never allowed
 * to fail the day. A registry that will not load leaves `summaryMetrics` empty,
 * so the derived rows simply do not render — the counts the desk actually plans
 * from are still on screen. That is deliberate: « Charge Ménage » is a
 * convenience computed from Départs; Départs is the fact.
 */
export function useChargeConfig(): { registry: MetricRegistry; specs: StaffingSpec[] } {
  const metricRegistry = useMetricRegistry();
  const staffingConfig = useStaffingConfig();
  const [metrics, setMetrics] = useState<MetricDef[]>([]);
  const [snapshot, setSnapshot] = useState<StaffingConfigSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const defs = await metricRegistry.load();
        if (!cancelled) setMetrics(defs);
      } catch {
        if (!cancelled) setMetrics([]);
      }
      try {
        const snap = await staffingConfig.load();
        if (!cancelled) setSnapshot(snap);
      } catch {
        if (!cancelled) setSnapshot(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [metricRegistry, staffingConfig]);

  const registry = useMemo(() => buildRegistry(metrics), [metrics]);
  const specs = useMemo(
    () =>
      snapshot
        ? staffingConfigFromRows({
            profiles: snapshot.profiles,
            bands: snapshot.bands,
            posts: snapshot.posts,
            workContent: snapshot.workContent,
          }).specs
        : [],
    [snapshot],
  );
  return { registry, specs };
}

/** Recouches = Nr = No − Na, clamped — the rooms half of the 0041 chain. */
function recouchesOf(day: BoardDay | null | undefined): number | null {
  if (!day || day.occupancy == null) return null;
  return Math.max(0, day.occupancy - day.arrivals);
}

/**
 * The flagged summary metrics for one stored day (« Charge Ménage » and any
 * sibling), formatted exactly as reception formats them: the NUMBER alone,
 * because the label beside it already carries the unit.
 */
function summaryFor(
  registry: MetricRegistry,
  specs: StaffingSpec[],
  defs: MetricDef[],
  day: BoardDay,
): Map<string, string | null> {
  const ctx = {
    day,
    profileOutputs: profileOutputsForDay({
      specs,
      day,
      workload: workloadFromDay(day),
      readyBy: checkInTime(day.arrivals),
    }),
  };
  const memo = new Map<string, number | null>();
  const row = new Map<string, string | null>();
  for (const def of defs) {
    const value = evaluateMetric(registry, def.key, ctx, memo).value;
    row.set(def.key, value === null ? null : formatMetricValue(value, def.unit));
  }
  return row;
}

const num = (n: number | null | undefined): string => (n == null ? '—' : String(n));

/**
 * The four room figures, the heads under them, and the restaurant counts, for
 * ONE day. Same row order the operator locked on 2026-08-01 (Départs ·
 * Arrivées · Occupation · Recouches), so the desk and the phone read alike.
 *
 * `compact` is the phone variant: it narrows the cells, it does not drop a
 * figure. A staff member reading the board on a phone must see the same day the
 * desk sees — a truncated board is how two people end up planning from two
 * different numbers.
 */
export function ChargeDay({
  day,
  registry,
  specs,
  compact = false,
}: {
  day: BoardDay | null;
  registry: MetricRegistry;
  specs: StaffingSpec[];
  compact?: boolean;
}) {
  const summaryDefs = useMemo(() => summaryMetrics(registry, { derivedOnly: true }), [registry]);
  const summary = useMemo(
    () => (day ? summaryFor(registry, specs, summaryDefs, day) : null),
    [registry, specs, summaryDefs, day],
  );

  // Heads under the rooms. Absent entirely until the feed has written adults
  // for this day, and « — » for a column it left null — never `?? 0`, which
  // would put a confident empty hotel on every day nobody imported (0129).
  // Bare numbers, no « P » suffix (operator, 2026-09-05): the row label says
  // « Personnes ». « (7 E) » keeps its letter because that cell alone carries
  // two figures and nothing else tells them apart.
  const peopleCells = useMemo(() => {
    if (day?.guestsAdults == null) return null;
    const kids = day.guestsChildren;
    const guests = day.guestsAdults + (kids ?? 0);
    const arr = day.arrivalsGuests;
    return [
      { key: 'departures', text: num(day.departuresGuests) },
      { key: 'arrivals', text: num(arr) },
      { key: 'occupancy', text: kids != null && kids > 0 ? `${guests} (${kids} E)` : String(guests) },
      { key: 'stayovers', text: arr == null ? '—' : num(Math.max(0, guests - arr)) },
    ];
  }, [day]);

  return (
    <section className={`board-demand${compact ? ' charge-compact' : ''}`}>
      <h3 className="board-section-title">
        Charge
        <FeedStamp />
        {!day && <span className="board-tag board-tag-warn">non saisie</span>}
      </h3>

      <div className="board-demand-row">
        <span className="board-demand-rowlabel">Hôtel</span>
        {DEMAND_HOTEL_FIELDS.map((f) => (
          <div
            key={f.key}
            className="board-demand-cell is-derived is-fed"
            style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
            title={FEED_HINT}
          >
            <span>{f.label}</span>
            <output>{num(day?.[f.key])}</output>
          </div>
        ))}
        <div
          className="board-demand-cell is-derived"
          style={{ '--metric-color': metricColor('stayovers') } as CSSProperties}
        >
          <span>Recouches</span>
          <output>{recouchesOf(day) ?? '—'}</output>
        </div>
        {summaryDefs.map((def) => (
          <div
            key={def.key}
            className="board-demand-cell is-derived"
            style={{ '--metric-color': metricColor(def.key) } as CSSProperties}
            title={def.labelFr}
          >
            <span>{def.labelFr}</span>
            <output>{summary?.get(def.key) ?? '—'}</output>
          </div>
        ))}
      </div>

      {peopleCells && (
        <div className="board-demand-row is-people" title={GUESTS_HINT}>
          <span className="board-demand-rowlabel">Personnes</span>
          {peopleCells.map((c) => (
            <div
              key={c.key}
              className="board-demand-cell is-derived"
              style={{ '--metric-color': metricColor(c.key) } as CSSProperties}
            >
              <output>{c.text}</output>
            </div>
          ))}
        </div>
      )}

      <div className="board-demand-row is-restaurant">
        <span className="board-demand-rowlabel">Restaurant</span>
        {DEMAND_RESTAURANT_FIELDS.map((f) => (
          <div
            key={f.key}
            className={`board-demand-cell is-derived${f.feedOwned ? ' is-fed' : ''}`}
            style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
            title={f.feedOwned ? FEED_HINT : undefined}
          >
            <span>{f.label}</span>
            <output>{num(day?.[f.key])}</output>
          </div>
        ))}
        <div
          className="board-demand-cell is-derived"
          style={{ '--metric-color': metricColor('covers') } as CSSProperties}
        >
          <span>Couverts</span>
          <output>
            {day ? day.halfBoards + day.dinersHotel + day.diners : '—'}
          </output>
        </div>
      </div>
    </section>
  );
}

/**
 * Short row headings for the phone table. Seven columns is the unit of a week,
 * so the only width left to win back is the heading column — and
 * « Petits-déjeuners » is wider than the seven counts it labels put together.
 * The full label stays on `title`, one press away.
 */
const SHORT_LABEL: Record<string, string> = {
  departures: 'Dép.',
  arrivals: 'Arr.',
  occupancy: 'Occ.',
  stayovers: 'Rec.',
  breakfasts: 'P.déj.',
  packedLunches: 'Pan.',
  halfBoards: 'DP',
  dinersHotel: 'Dîn.',
  diners: 'Dîn. ext',
  covers: 'Couv.',
};

/**
 * « Charge de la semaine », read-only — the desk's week grid over stored days.
 *
 * Answering the operator's *"can it fit a phone format?"*: yes, but only as a
 * table. Seven days IS the unit of the thing; stacking seven day cards would be
 * seven Jour views, which is not a week — the whole reason to look at a week is
 * to compare Thursday with Saturday in one glance. So the compact variant
 * shrinks the type, trims the padding and abbreviates the row headings, and
 * keeps `.week-scroll`'s horizontal scroll underneath as the honest fallback on
 * the narrowest phones rather than pretending every screen fits.
 */
export function ChargeWeek({
  days,
  byDay,
  registry,
  specs,
  compact = false,
}: {
  days: readonly string[];
  byDay: ReadonlyMap<string, BoardDay>;
  registry: MetricRegistry;
  specs: StaffingSpec[];
  compact?: boolean;
}) {
  const summaryDefs = useMemo(() => summaryMetrics(registry, { derivedOnly: true }), [registry]);
  const summaryByDay = useMemo(() => {
    const map = new Map<string, Map<string, string | null>>();
    if (summaryDefs.length === 0) return map;
    for (const key of days) {
      const day = byDay.get(key);
      if (day) map.set(key, summaryFor(registry, specs, summaryDefs, day));
    }
    return map;
  }, [summaryDefs, days, byDay, registry, specs]);

  const label = (key: string, full: string): string => (compact ? (SHORT_LABEL[key] ?? full) : full);
  const cell = (key: string, read: (d: BoardDay) => number | null): string => {
    const day = byDay.get(key);
    return day ? num(read(day)) : '—';
  };

  return (
    <section className={`board-demand${compact ? ' charge-compact' : ''}`}>
      <h3 className="board-section-title">
        Charge de la semaine
        <FeedStamp />
      </h3>
      <div className="week-scroll">
        <table className="week-demand-table">
          <thead>
            <tr>
              <th />
              {days.map((key) => (
                <th key={key}>{fmtShortDayLabel(key)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="week-demand-group">
              <th colSpan={days.length + 1}>Hôtel</th>
            </tr>
            {DEMAND_HOTEL_FIELDS.map((f) => (
              <tr
                key={f.key}
                className="week-demand-derived is-fed"
                style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                title={FEED_HINT}
              >
                <th title={f.label}>{label(f.key, f.label)}</th>
                {days.map((key) => (
                  <td key={key}>{cell(key, (d) => d[f.key])}</td>
                ))}
              </tr>
            ))}
            <tr
              className="week-demand-derived"
              style={{ '--metric-color': metricColor('stayovers') } as CSSProperties}
            >
              <th title="Recouches">{label('stayovers', 'Recouches')}</th>
              {days.map((key) => (
                <td key={key}>{recouchesOf(byDay.get(key)) ?? '—'}</td>
              ))}
            </tr>
            {summaryDefs.map((def) => (
              <tr
                key={def.key}
                className="week-demand-derived"
                style={{ '--metric-color': metricColor(def.key) } as CSSProperties}
              >
                <th title={def.labelFr}>{def.labelFr}</th>
                {days.map((key) => (
                  <td key={key}>{summaryByDay.get(key)?.get(def.key) ?? '—'}</td>
                ))}
              </tr>
            ))}

            <tr className="week-demand-group">
              <th colSpan={days.length + 1}>Restaurant</th>
            </tr>
            {DEMAND_RESTAURANT_FIELDS.map((f) => (
              <tr
                key={f.key}
                className={`week-demand-derived${f.feedOwned ? ' is-fed' : ''}`}
                style={{ '--metric-color': metricColor(f.key) } as CSSProperties}
                title={f.feedOwned ? FEED_HINT : undefined}
              >
                <th title={f.label}>{label(f.key, f.label)}</th>
                {days.map((key) => (
                  <td key={key}>{cell(key, (d) => d[f.key])}</td>
                ))}
              </tr>
            ))}
            <tr
              className="week-demand-derived"
              style={{ '--metric-color': metricColor('covers') } as CSSProperties}
            >
              <th title="Couverts">{label('covers', 'Couverts')}</th>
              {days.map((key) => (
                <td key={key}>
                  {cell(key, (d) => d.halfBoards + d.dinersHotel + d.diners)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}
